import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, XCircle, PauseCircle, PlayCircle, FileText, ShieldCheck, X } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import AdminLayout from '@/components/AdminLayout';

const STATUS_TABS = [
  { key: 'all',       label: '전체' },
  { key: 'pending',   label: '심사 중' },
  { key: 'approved',  label: '승인' },
  { key: 'rejected',  label: '반려' },
  { key: 'suspended', label: '이용 정지' },
];

const STATUS_LABELS = {
  pending: '심사 중', approved: '승인', rejected: '반려', suspended: '이용 정지',
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending:   'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    approved:  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    rejected:  'bg-red-500/10 text-red-600 dark:text-red-400',
    suspended: 'bg-gray-500/10 text-gray-600 dark:text-gray-300',
  };
  return <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full ${styles[status] || 'bg-black/5 dark:bg-white/10'}`} data-testid={`status-badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
};

const DetailModal = ({ application, onClose, onAction }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [adminMemo, setAdminMemo] = useState(application?.application?.admin_memo || '');
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [error, setError] = useState('');

  if (!application) return null;
  const app = application.application;
  const user = application.user;
  const history = application.history || [];
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await fn();
      toast.success(successMsg);
      await onAction();
      onClose();
    }
    catch (e) {
      const msg = e?.response?.data?.detail || '요청에 실패했습니다. 다시 시도해주세요.';
      setError(msg);
      toast.error(msg);
    }
    finally { setBusy(false); }
  };

  const approve = () => run(() => api.post(`/admin/seller-applications/${app.application_id}/approve`, { admin_memo: adminMemo }), '판매자가 승인되었습니다.');
  const reject = () => {
    if (!rejectReason.trim()) { setError('반려 사유를 입력해주세요.'); return; }
    run(() => api.post(`/admin/seller-applications/${app.application_id}/reject`, { rejection_reason: rejectReason, admin_memo: adminMemo }), '반려 처리되었습니다.');
  };
  const suspend = () => run(() => api.post(`/admin/seller-applications/${app.application_id}/suspend`, { admin_memo: adminMemo }), '이용 정지 처리되었습니다.');
  const unsuspend = () => run(() => api.post(`/admin/seller-applications/${app.application_id}/unsuspend`), '이용 정지가 해제되었습니다.');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-detail-modal">
      <div className="bg-white dark:bg-[#141414] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black/5 dark:border-white/10 sticky top-0 bg-white dark:bg-[#141414] z-10">
          <div>
            <h3 className="text-xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{app.seller_name}</h3>
            <p className="text-xs text-gray-500 mt-1">{app.application_id} · {format(new Date(app.submitted_at), 'yyyy-MM-dd HH:mm', { locale: ko })}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={app.status} />
            <button onClick={onClose} className="p-2"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {error && <div className="p-3 rounded bg-[#FF3B30]/10 text-[#FF3B30] text-sm font-bold" data-testid="admin-detail-error">{error}</div>}

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="admin-detail-basic">
            <div><span className="text-gray-500">대표자</span> · {app.representative_name}</div>
            <div><span className="text-gray-500">연락처</span> · {app.phone}</div>
            <div><span className="text-gray-500">이메일</span> · {app.contact_email}</div>
            <div><span className="text-gray-500">계정</span> · {user?.email}</div>
            <div><span className="text-gray-500">대표 카테고리</span> · {app.primary_category}</div>
            <div><span className="text-gray-500">플랫폼</span> · {(app.platforms || []).join(', ')}</div>
          </section>

          <section>
            <p className="text-xs font-bold text-gray-500 mb-1">플랫폼 URL</p>
            <div className="space-y-1 text-sm">
              {Object.entries(app.platform_urls || {}).map(([p, u]) => (
                <a key={p} href={u} target="_blank" rel="noreferrer" className="block text-[#FF3B30] hover:underline break-all">{p}: {u}</a>
              ))}
            </div>
          </section>

          <section>
            <p className="text-xs font-bold text-gray-500 mb-1">소개</p>
            <p className="text-sm whitespace-pre-wrap">{app.introduction}</p>
          </section>

          <section>
            <p className="text-xs font-bold text-gray-500 mb-1">신청 사유</p>
            <p className="text-sm whitespace-pre-wrap">{app.application_reason}</p>
          </section>

          {app.profile_image_url && (
            <section>
              <p className="text-xs font-bold text-gray-500 mb-1">프로필 이미지</p>
              <SafeImg src={app.profile_image_url} kind="seller_profile" alt="profile" className="w-24 h-24 rounded-full object-cover" />
            </section>
          )}

          {(app.business_name || app.business_registration_number || app.business_document_url) && (
            <section className="p-4 bg-black/5 dark:bg-white/5 rounded" data-testid="admin-detail-business">
              <p className="text-xs font-bold text-gray-500 mb-2">사업자 정보</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 text-sm">
                <div><span className="text-gray-500">상호명</span> · {app.business_name || '-'}</div>
                <div><span className="text-gray-500">사업자번호</span> · {app.business_registration_number || '-'}</div>
                <div><span className="text-gray-500">통신판매업</span> · {app.mail_order_registration_number || '-'}</div>
              </div>
              {app.business_document_url && (
                <a href={`${BACKEND}${app.business_document_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#FF3B30] hover:underline" data-testid="admin-detail-business-doc">
                  <FileText size={14} /> 사업자 등록증 확인
                </a>
              )}
            </section>
          )}

          {history.length > 1 && (
            <section data-testid="admin-detail-history">
              <p className="text-xs font-bold text-gray-500 mb-2">신청 이력 ({history.length}건)</p>
              <div className="space-y-1 text-xs">
                {history.map((h) => (
                  <div key={h.application_id} className="flex items-center justify-between px-3 py-2 bg-black/5 dark:bg-white/5 rounded">
                    <span>{format(new Date(h.submitted_at), 'yyyy-MM-dd HH:mm', { locale: ko })}</span>
                    <StatusBadge status={h.status} />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <p className="text-xs font-bold text-gray-500 mb-1">관리자 메모</p>
            <textarea rows={3} value={adminMemo} onChange={(e) => setAdminMemo(e.target.value)} className="w-full px-3 py-2 text-sm border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" placeholder="심사 메모" data-testid="admin-memo-input" />
          </section>

          {showReject && (
            <section>
              <p className="text-xs font-bold text-red-600 mb-1">반려 사유 (필수)</p>
              <textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full px-3 py-2 text-sm border rounded bg-white dark:bg-[#0A0A0A] border-red-300" data-testid="admin-reject-reason" />
            </section>
          )}
        </div>

        <div className="p-5 border-t border-black/5 dark:border-white/10 flex flex-wrap gap-2 sticky bottom-0 bg-white dark:bg-[#141414]" data-testid="admin-detail-actions">
          {(app.status === 'pending' || app.status === 'rejected') && (
            <>
              <button onClick={approve} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold rounded-full" data-testid="admin-approve-btn"><CheckCircle2 size={14} />{app.status === 'rejected' ? '재검토 후 승인' : '승인'}</button>
              {app.status === 'pending' && (
                <button onClick={() => setShowReject((v) => !v)} className="inline-flex items-center gap-1 px-4 py-2 bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold rounded-full" data-testid="admin-reject-toggle"><XCircle size={14} />반려</button>
              )}
              {showReject && (
                <button onClick={reject} disabled={busy} className="px-4 py-2 bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white text-sm font-bold rounded-full" data-testid="admin-reject-confirm">반려 확정</button>
              )}
            </>
          )}
          {app.status === 'approved' && (
            <button onClick={suspend} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white text-sm font-bold rounded-full" data-testid="admin-suspend-btn"><PauseCircle size={14} />이용 정지</button>
          )}
          {app.status === 'suspended' && (
            <button onClick={unsuspend} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold rounded-full" data-testid="admin-unsuspend-btn"><PlayCircle size={14} />이용 정지 해제</button>
          )}
          <button onClick={onClose} className="px-4 py-2 border border-black/10 dark:border-white/10 text-sm font-bold rounded-full ml-auto">닫기</button>
        </div>
      </div>
    </div>
  );
};

const AdminSellerApplications = ({ user }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setDenied(false);
    try {
      const res = await api.get('/admin/seller-applications', { params: { status, q: q || undefined } });
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin/seller-applications/${id}`);
      setSelected(res.data);
    } catch (e) { /* ignore */ }
  };

  if (!user) return null;

  if (denied) {
    return (
      <AdminLayout user={user} active="applications">
        <div className="text-center py-16" data-testid="admin-denied">
          <h2 className="text-2xl font-black mb-2">관리자 권한이 없습니다.</h2>
          <p className="text-sm text-gray-500">관리자 권한을 가진 계정으로 로그인해주세요.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user} active="applications">
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF3B30] mb-2"><ShieldCheck size={14} /> 관리자</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>판매자 신청 관리</h1>
          <p className="text-sm text-gray-500 mt-2">신청서를 검토하고 승인, 반려, 이용 정지 등을 처리할 수 있습니다.</p>
        </header>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((t) => (
              <button key={t.key} onClick={() => setStatus(t.key)} data-testid={`admin-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-bold border ${status === t.key ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto flex-1 sm:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="셀러명, 대표자, 이메일, 연락처 검색" className="w-full pl-9 pr-3 py-2 border rounded-full bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm" data-testid="admin-search-input" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-sm text-gray-500">불러오는 중...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500" data-testid="admin-empty">신청 내역이 없습니다.</div>
          ) : (
            <div className="divide-y divide-black/5 dark:divide-white/10" data-testid="admin-application-list">
              {items.map((a) => (
                <button key={a.application_id} onClick={() => openDetail(a.application_id)}
                  className="w-full text-left px-5 py-4 hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex items-center gap-4"
                  data-testid={`admin-application-row-${a.application_id}`}>
                  <SafeImg src={a.profile_image_url} kind="seller_profile" alt={a.seller_name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm truncate">{a.seller_name}</p>
                      <StatusBadge status={a.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{a.representative_name} · {a.user_email} · {a.phone}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{format(new Date(a.submitted_at), 'yyyy-MM-dd', { locale: ko })}</p>
                    <p className="text-xs text-gray-400">{a.primary_category}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <DetailModal application={selected} onClose={() => setSelected(null)} onAction={load} />
    </AdminLayout>
  );
};

export default AdminSellerApplications;
